// RedirectWithParams.tsx
//
// Wave C / C3 (W2 route-dedup).
//
// React Router v7's <Navigate to="..."> does NOT interpolate `:params` in the
// target path, so a naive <Navigate to="/ipc/:ipcId" /> would push the literal
// string ":ipcId". This helper reads the current route params (and search/hash)
// and builds the concrete canonical URL before redirecting — so old bookmarked
// URLs like /ipcs/42?tab=items resolve to /ipc/42?tab=items with NO dead link.
//
// Usage in App.tsx:
//   <Route path="/ipcs/:ipcId" element={<RedirectWithParams to="/ipc/:ipcId" />} />
//   <Route path="/ipcs"        element={<Navigate to="/ipc" replace />} />
//
// `to` may contain `:paramName` tokens; each is replaced with the matching
// value from useParams(). Query string and hash are preserved verbatim.

import { Navigate, useParams, useLocation } from 'react-router-dom'

interface RedirectWithParamsProps {
  /** Canonical target path; may contain `:paramName` tokens to interpolate. */
  to: string
}

export function buildRedirectTarget(
  to: string,
  params: Readonly<Record<string, string | undefined>>,
  search = '',
  hash = '',
): string {
  const path = to.replace(/:([A-Za-z0-9_]+)/g, (_match, name: string) => {
    const value = params[name]
    // Fall back to the literal token if a param is somehow missing, rather
    // than emitting "undefined" into the URL.
    return value !== undefined ? encodeURIComponent(value) : `:${name}`
  })
  return `${path}${search}${hash}`
}

export default function RedirectWithParams({ to }: RedirectWithParamsProps) {
  const params = useParams()
  const location = useLocation()
  const target = buildRedirectTarget(to, params, location.search, location.hash)
  return <Navigate to={target} replace />
}
